import * as React from 'react';

import { cn } from '@/lib/utils';

export interface GradientBorderProps {
  /**
   * Enables rotating gradient animation (only the border rotates, not content)
   */
  animated?: boolean;
  /**
   * Triggers thicker border (2px vs 1.25px)
   */
  focused?: boolean;
  /**
   * Triggers thicker border (2px vs 1.25px)
   */
  active?: boolean;
  /**
   * Disables gradient, shows muted border
   */
  disabled?: boolean;
  /**
   * Border radius class, defaults to 'rounded-full'
   */
  borderRadius?: string;
  /**
   * Outer wrapper classes
   */
  className?: string;
  /**
   * Inner content wrapper classes
   */
  innerClassName?: string;
  /**
   * Content to render inside the gradient border
   */
  children: React.ReactNode;
}

/**
 * GradientBorder - A shared primitive that provides an animated gradient border effect.
 * Uses the same gradient as AIInput: linear-gradient from oasis-300 via air-400 to air-600.
 * When animated, a conic-gradient spins around the border while content stays stable.
 */
const GradientBorder = React.forwardRef<HTMLDivElement, GradientBorderProps>(
  (
    {
      animated = false,
      focused = false,
      active = false,
      disabled = false,
      borderRadius = 'rounded-full',
      className,
      innerClassName,
      children,
    },
    ref,
  ) => {
    // Show gradient if focused, active, OR animated (animated implies wanting to see gradient)
    const isActive = (focused || active || animated) && !disabled;
    const borderWidth = isActive ? 2 : 1.25;
    const showGradient = isActive;

    return (
      <div
        ref={ref}
        data-slot="gradient-border"
        className={cn(
          'relative w-full overflow-hidden transition-all',
          borderRadius,
          // Base background color to prevent white gaps during animation
          !disabled && showGradient && 'bg-air-400',
          disabled && 'bg-surface-2',
          !showGradient && 'bg-surface-2',
          className,
        )}
        style={{ padding: `${borderWidth}px` }}
      >
        {/* Static gradient background (matches AIInput exactly) - shown when not animated */}
        {showGradient && !animated && (
          <div
            className={cn(
              'absolute inset-0',
              borderRadius,
              'bg-linear-to-b from-oasis-300 via-air-400 to-air-600',
            )}
          />
        )}

        {/* Animated spinning gradient (only visible when animated) */}
        {/* Wrapper centers the spinner, inner div rotates independently */}
        {animated && !disabled && showGradient && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className="aspect-square animate-spin motion-reduce:animate-none"
              style={{
                animationDuration: '2s',
                width: 'max(500%, 500px)',
                background:
                  'conic-gradient(from 0deg, var(--oasis-300), var(--air-400), var(--air-600), var(--oasis-300))',
              }}
            />
          </div>
        )}

        {/* Inner content container */}
        <div
          className={cn(
            'relative bg-background w-full h-full',
            borderRadius,
            disabled && 'bg-muted',
            innerClassName,
          )}
        >
          {children}
        </div>
      </div>
    );
  },
);

GradientBorder.displayName = 'GradientBorder';

export { GradientBorder };
